import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@fittrack/ui';
import { MemberProvider, useMember } from './context/MemberContext';
import { AppInfoProvider } from './context/AppContext';
import { SiteProvider, parseSiteNameFromUrl } from './context/SiteContext';
import apiClient, { postClientError } from './api/client';
import { config } from './config';
import ErrorBoundary from './components/ErrorBoundary';
import BottomNav from './components/BottomNav';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import HomePage from './pages/HomePage';
import QrPage from './pages/QrPage';
import SettingsPage from './pages/SettingsPage';
import CreateUserPage from './pages/CreateUserPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EnrollPage from './pages/EnrollPage';
import EnrollJoinStep1Page from './pages/EnrollJoinStep1Page';
import EnrollJoinStep2Page from './pages/EnrollJoinStep2Page';
import EnrollConfirmationPage from './pages/EnrollConfirmationPage';
import SchedulePreviewPage from './pages/SchedulePreviewPage';
import EmailPage from './pages/EmailPage';
import './App.css';

function isAuthenticated() {
  return !!sessionStorage.getItem('apiKey');
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />;
}

function AppShell({ children }: { children: React.ReactNode }) {
  const { member, clearMember } = useMember();
  const navigate = useNavigate();

  // Authenticated pages: use the person-specific logo from their site
  // (CustomerLogo joined via AccountPeople → Account → Site for this PersonID).
  const memberLogo = member?.logoUrl ?? null;

  async function handleLogout() {
    try { await apiClient.post('/Authentication/Logout'); } catch { /* ignore */ }
    clearMember();
    navigate('/login', { replace: true });
  }

  const memberAppName = member?.appName ?? null;

  return (
    <ThemeProvider
      applicationType="Online"
      baseURL={config.apiUrl}
      apiKey={sessionStorage.getItem('apiKey')}
      tenantId={tenantName}
      siteId={member?.siteId}
      requireAuth={false}
    >
      <div className="app-shell">
        <header className="app-topbar">
          <div className="app-topbar__left">
            {memberLogo && (
              <img src={memberLogo} alt="Logo" className="app-topbar__logo" />
            )}
          </div>
          {memberAppName && (
            <span className="app-topbar__title">{memberAppName}</span>
          )}
          <button className="app-topbar__logout" onClick={handleLogout} aria-label="Logout">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={22} height={22}>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </header>
        <main className="app-shell__content">{children}</main>
        <BottomNav />
      </div>
    </ThemeProvider>
  );
}

function NoTenant() {
  return (
    <div className="no-tenant">
      <p>No gym selected. Please use the link provided by your gym.</p>
    </div>
  );
}

function InvalidTenant() {
  return (
    <div className="no-tenant">
      <p>Gym not found. Please check the link provided by your gym.</p>
    </div>
  );
}

const tenantName = window.location.pathname.split('/').filter(Boolean)[0] ?? '';
const siteName   = parseSiteNameFromUrl();
const basename   = tenantName ? `/${tenantName}${siteName ? `/${siteName}` : ''}` : '/';

// Global JS error handlers — catch anything that escapes React's render cycle.
// postClientError is silently swallowed on failure so these never crash the app.
window.onerror = (_msg, source, line, col, error) => {
  postClientError({
    level: 'error',
    message: error?.message ?? String(_msg),
    stack: error?.stack ?? `${source}:${line}:${col}`,
    url: window.location.href,
  });
  return false; // let default browser handling continue
};

window.onunhandledrejection = (event) => {
  const err = event.reason;
  postClientError({
    level: 'error',
    message: err instanceof Error ? err.message : String(err),
    stack: err instanceof Error ? err.stack : undefined,
    url: window.location.href,
  });
};

// In production, config.js sets window.TENANT_API_URL. If it's missing for a given
// tenant path, the tenant doesn't exist. Allow dev mode (Vite proxy) and VITE_API_URL overrides.
const tenantConfigLoaded =
  !!window.TENANT_API_URL ||
  !!import.meta.env.VITE_API_URL ||
  import.meta.env.DEV;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/createuser" element={<CreateUserPage />} />
      <Route path="/email" element={<EmailPage />} />
      <Route path="/forgotpassword" element={<ForgotPasswordPage />} />
      <Route path="/resetpassword" element={<ResetPasswordPage />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <AppShell><HomePage /></AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/qr"
        element={
          <PrivateRoute>
            <AppShell><QrPage /></AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <PrivateRoute>
            <AppShell><SchedulePreviewPage /></AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/enroll"
        element={
          <PrivateRoute>
            <AppShell><EnrollPage /></AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/enroll/join/:packageId/step1"
        element={
          <PrivateRoute>
            <AppShell><EnrollJoinStep1Page /></AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/enroll/join/:packageId/step2"
        element={
          <PrivateRoute>
            <AppShell><EnrollJoinStep2Page /></AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/enroll/confirmation"
        element={
          <PrivateRoute>
            <AppShell><EnrollConfirmationPage /></AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <AppShell><SettingsPage /></AppShell>
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to={isAuthenticated() ? '/' : '/login'} replace />} />
    </Routes>
  );
}

export default function App() {
  if (!tenantName) return <NoTenant />;
  if (!tenantConfigLoaded) return <InvalidTenant />;

  return (
    <ErrorBoundary>
      <MemberProvider>
        <BrowserRouter basename={basename}>
          <SiteProvider>
            <AppInfoProvider>
              <AppRoutes />
            </AppInfoProvider>
          </SiteProvider>
        </BrowserRouter>
      </MemberProvider>
    </ErrorBoundary>
  );
}
